import { ReactElement, ReactNode, useState, useEffect, useRef } from 'react'
import cn from 'classnames'
import IconInputArrow from 'images/icon-input-arrow.svg'
import IconNewInputArrow from 'images/icon-input-new-arrow.svg'
import IconLoading from 'images/icon-loader-blue.svg'
import onlyNumberChanger from 'utils/only-numbers-value'
import { formatNumber } from 'utils/formatters'
import { adjustCursorPosition } from 'utils/input-cursor'

import Input from 'components/app/input'
import { NetworkLabel } from 'components/ui/network-label'

import { ICurrency, ICurrencyWithBalance } from 'store/fetchers/currencies'

import { useTranslation } from 'utils/use-translation'

import { CoinList } from '../coins-dropdown'

import { floorNumber } from '../../../../../utils/floor-number'

import { countDecimalPlaces } from '../../../../../utils/count-decimal-places'

import useOnClickOutside from '../../../../../hooks/use-on-click-outside'

import styles from './styles.module.scss'

export default function InputCalculator({
  onChangeAmount,
  onChangeCoin,
  value,
  rate,
  label,
  coin,
  list = [],
  children,
  isLoading,
  onEnter,
  isStableFirst = false,
  className = null,
  labelClassName = null,
  inputClassName = null,
  inputWrapperClassName = null,
  bigCurBlock = false,
  showBalance,
  listFullWidth = false,
  isExchangePage = false,
  isExchangeLayout = false,
  readonly = false,
  inputMode = 'decimal',
  loaderClassName = null,
}: {
  onChangeAmount: (value: string) => void
  onChangeCoin: (coin: ICurrency) => void
  value: string
  rate: string
  label: string
  coin: ICurrencyWithBalance
  list: ICurrencyWithBalance[]
  children: ReactNode
  isLoading: boolean
  onEnter?: () => void
  isStableFirst?: boolean
  className?: string
  labelClassName?: string
  inputClassName?: string
  inputWrapperClassName?: string
  bigCurBlock?: boolean
  showBalance?: boolean
  listFullWidth?: boolean
  isExchangePage?: boolean
  isExchangeLayout?: boolean
  readonly?: boolean
  inputMode?: 'text' | 'email' | 'search' | 'none' | 'tel' | 'url' | 'numeric' | 'decimal'
  loaderClassName?: string
}): ReactElement {
  const { t } = useTranslation()

  const [isVisibleList, setVisibleList] = useState(false)
  const isError = false
  const formattedLabel = label.replace(' ', '_').toLowerCase()
  const [formattedValue, setFormattedValue] = useState(value)
  const [screenWidth, setScreenWidth] = useState<number>(0)
  const ref = useRef(null)

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setScreenWidth(window.innerWidth)
    }
  }, [])

  useEffect(() => {
    const strValue = value?.toString()
    setFormattedValue(formatNumber(strValue?.replace(/\s/g, '')))
  }, [value, rate])

  const handleChangeAmount = (event) => {
    if (coin) {
      const rawValue = event.target.value.replace(/\s/g, '')
      const parts = rawValue.split('.')
      if (parts[1]?.length > coin.decimal_places) {
        return
      }

      let cursorPosition = event.target.selectionStart
      let newValue = ''

      if (event.nativeEvent.inputType === 'deleteContentBackward') {
        cursorPosition += 1
      } else if (event.nativeEvent.inputType === 'deleteContentForward') {
        cursorPosition += 1
      } else {
        cursorPosition -= 1
      }

      onlyNumberChanger(rawValue, (newRawValue) => {
        newValue = newRawValue

        const newFormattedValue = formatNumber(newValue)
        setFormattedValue(newFormattedValue)

        setTimeout(() => {
          if (inputRef.current) {
            const newCursorPosition = cursorPosition - (formattedValue.length - newFormattedValue.length)
            inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
          }
        }, 0)
      })
      onlyNumberChanger(rawValue, onChangeAmount)
    }
  }

  const handleCoinListClose = () => {
    setVisibleList(false)
  }

  const handleChooseCoin = (coin) => {
    onChangeCoin(coin)
    handleCoinListClose()
  }

  useOnClickOutside(ref, () => {
    setVisibleList(false)
  })

  return (
    <div ref={ref} className={cn(styles.changeCoinFrom, className, { [styles.exchangePage]: isExchangePage })}>
      <label
        className={cn(styles.name, labelClassName, { [styles.exchangeLayout]: isExchangeLayout })}
        htmlFor={formattedLabel}
      >
        {label}
      </label>
      <div
        className={cn(
          styles.inputBox,
          inputWrapperClassName,
          { [styles.inputBox_exchangeLayout]: isExchangeLayout },
          { [styles.inputBoxOpened]: isVisibleList },
          { [styles.inputErrorValue]: isError },
        )}
      >
        <div className={styles.leftData}>
          <Input
            ref={inputRef}
            className={cn(
              styles.inputField,
              inputClassName,
              { [styles.inputFieldOpened]: isVisibleList },
              { [styles.exchangeLayout]: isExchangeLayout },
            )}
            inputMode={inputMode}
            onEnter={onEnter}
            value={isLoading ? '' : formattedValue}
            onChange={handleChangeAmount}
            onKeyDown={adjustCursorPosition}
            onMouseUp={adjustCursorPosition}
            id={formattedLabel}
            readOnly={readonly}
          />
        </div>

        {isLoading && <img src={IconLoading} className={cn(styles.loader, loaderClassName)} alt="loading" />}
        {children}
        <div className={styles.openListButton} onClick={() => setVisibleList((st) => !st)}>
          {coin && coin.code && (
            <div
              className={cn(
                styles.currency,
                { [styles.currency_bigMode]: bigCurBlock },
                { [styles.exchangePage]: isExchangePage },
              )}
            >
              <img
                className={cn(
                  styles.currencyIcon,
                  { [styles.currencyIcon_bigMode]: bigCurBlock },
                  { [styles.exchangePage]: isExchangePage },
                )}
                alt={coin.code}
                src={coin.logo_url}
                width={bigCurBlock ? 36 : 20}
                height={bigCurBlock ? 36 : 20}
              />
              <div className={styles.column}>
                <div className={styles.mainData}>
                  <div className={styles.mainData__coin}>
                    {coin?.code} <NetworkLabel className={styles.network} currency={coin} />
                  </div>
                  <div className={cn(styles.balance, { [styles.balance_bigMode]: bigCurBlock })}>
                    {showBalance
                      ? coin?.balance && coin.balance > 0
                        ? `${t('balance')}: ` +
                          floorNumber(+Number(coin.balance).toFixed(countDecimalPlaces(coin?.balance)), 6)
                        : `${t('balance')}: ` + 0
                      : ''}
                  </div>
                </div>
              </div>
            </div>
          )}
          {list.length > 0 && (
            <img
              src={bigCurBlock ? IconNewInputArrow : IconInputArrow}
              className={cn({ [styles.openedListArrow]: isVisibleList }, { [styles.iconArrow]: bigCurBlock })}
              alt="IconInputArrow"
              width={8}
              height={4}
            />
          )}
        </div>
        <CoinList
          className={styles.list}
          list={list}
          showBalance={showBalance}
          selected={coin}
          onChoose={handleChooseCoin}
          isStableFirst={isStableFirst}
          listFullWidth={screenWidth > 765 ? listFullWidth : true}
          isExchangeLayout={isExchangeLayout}
          isVisibleList={isVisibleList}
        />
      </div>
    </div>
  )
}
